'use client'

import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { CircleHelpIcon } from 'lucide-react'

import { cn } from '@/components/new/lib/utils'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/new/ui/popover'

type LabelProps = React.ComponentProps<'label'> & {
    hint?: React.ReactNode
    required?: boolean
    'data-slot'?: string
}

function Label({ className, hint, required, children, 'data-slot': dataSlot = 'label', ...props }: LabelProps) {
    const { t } = useTranslation()
    const labelClassName = cn(
        'flex items-center gap-2 text-sm leading-none font-medium select-none group-data-[disabled=true]:pointer-events-none group-data-[disabled=true]:opacity-50 peer-disabled:cursor-not-allowed peer-disabled:opacity-50',
        className
    )

    if (hint) {
        return (
            <span data-slot={dataSlot} className={labelClassName}>
                <label className="contents" {...props}>
                    {children}
                    {required && <span className="text-destructive">*</span>}
                </label>
                <Popover>
                    <PopoverTrigger
                        nativeButton={true}
                        render={
                            <button
                                type="button"
                                className="text-muted-foreground hover:text-foreground inline-flex size-4 items-center justify-center rounded-sm"
                                aria-label={t('components.label.showHint')}
                                onClick={(event) => {
                                    // Keep label click behavior for text, but prevent input focus when hint icon is clicked.
                                    event.preventDefault()
                                    event.stopPropagation()
                                }}
                            >
                                <CircleHelpIcon aria-hidden className="size-3.5" />
                            </button>
                        }
                    />
                    <PopoverContent align="start" className="max-w-72">
                        {hint}
                    </PopoverContent>
                </Popover>
            </span>
        )
    }

    return (
        <label data-slot={dataSlot} className={labelClassName} {...props}>
            {children}
            {required && <span className="text-destructive">*</span>}
        </label>
    )
}

export { Label }
